<template>
  <v-container
    fluid
    class="tf-contact-section py-8 py-md-16"
    id="tf-contact"
  >
    <v-container>
      <v-row>
        <v-col cols="12" class="text-center mb-8">
          <span
            class="overline font-weight-bold"
            :style="{ color: '#00695C' }"
            data-aos="fade-down"
          >
            EMPEZÁ AHORA
          </span>
          <h2
            class="display-1 greydark--text font-weight-bold mt-2"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Solicitá tu demo gratuita de TradeFlow
          </h2>
          <p
            class="text-h6 greylight--text mt-4"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Mostramos cómo TradeFlow se adapta a tu operación
            de comercio exterior en 15 minutos.
          </p>
        </v-col>
      </v-row>

      <v-row class="d-flex justify-center align-center">
        <v-col
          cols="12"
          md="5"
          class="d-flex flex-column align-center justify-center"
          data-aos="fade-right"
          data-aos-delay="300"
        >
          <h3
            class="text-h4 greydark--text mb-4 text-center font-weight-bold"
          >
            ¿Qué incluye la demo?
          </h3>
          <p
            class="text-body-1 greylight--text text-center"
            style="max-width: 400px;"
          >
            Una sesión personalizada donde configuramos
            TradeFlow con datos de tu operación real.
          </p>

          <v-list class="transparent mt-6">
            <v-list-item
              v-for="(item, index) in demoFeatures"
              :key="index"
            >
              <v-list-item-icon>
                <v-icon :style="{ color: '#00695C' }">
                  {{ item.icon }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title
                  class="greydark--text font-weight-medium"
                >
                  {{ item.text }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>

        <v-col
          cols="12"
          md="6"
          data-aos="fade-left"
          data-aos-delay="300"
        >
          <v-card class="pa-8 rounded-lg" elevation="5">
            <v-form ref="contactForm" @submit.prevent="submitForm">
              <v-text-field
                v-model="form.name"
                label="Nombre completo"
                outlined
                required
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'El nombre es requerido']"
              ></v-text-field>

              <v-text-field
                v-model="form.company"
                label="Empresa"
                outlined
                required
                prepend-inner-icon="mdi-domain"
                :rules="[v => !!v || 'La empresa es requerida']"
              ></v-text-field>

              <v-text-field
                v-model="form.email"
                label="Correo electrónico"
                type="email"
                outlined
                required
                prepend-inner-icon="mdi-email"
                :rules="emailRules"
              ></v-text-field>

              <v-text-field
                v-model="form.phone"
                label="Teléfono / WhatsApp (opcional)"
                outlined
                prepend-inner-icon="mdi-whatsapp"
                placeholder="Ej: +52 55 1234 5678"
              ></v-text-field>

              <v-select
                v-model="form.operationType"
                :items="operationTypes"
                label="Tipo de operación"
                outlined
                prepend-inner-icon="mdi-swap-horizontal"
                required
                :rules="[v => !!v || 'Seleccioná tu tipo de operación']"
              ></v-select>

              <v-text-field
                v-model="form.subject"
                label="¿Cuántos embarques gestionás por mes?"
                outlined
                prepend-inner-icon="mdi-ship-wheel"
                required
                placeholder="Ej: 10-20 embarques de exportación"
                :rules="[v => !!v || 'Este campo es requerido']"
              ></v-text-field>

              <v-textarea
                v-model="form.message"
                label="Contanos sobre tu operación"
                outlined
                rows="4"
                prepend-inner-icon="mdi-message-text"
                required
                placeholder="Ej: Exportamos alimentos a Europa
                  y necesitamos automatizar BLs..."
                :rules="[v => !!v || 'Este campo es requerido']"
              ></v-textarea>

              <v-btn
                type="submit"
                :x-large="$vuetify.breakpoint.mdAndUp"
                :large="$vuetify.breakpoint.smAndDown"
                block
                depressed
                class="white--text"
                :style="{ backgroundColor: '#00695C' }"
                :loading="submitting"
                rounded
              >
                Solicitar Demo Gratis
              </v-btn>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success Dialog -->
    <v-dialog
      v-model="showSuccessDialog"
      max-width="500"
    >
      <v-card class="rounded-lg">
        <v-card-title class="d-flex flex-column align-center pt-8">
          <v-avatar
            size="80"
            class="mb-4"
            :style="{ backgroundColor: '#00695C' }"
          >
            <v-icon size="50" color="white">mdi-check-circle</v-icon>
          </v-avatar>
          <span
            class="text-h5 greydark--text font-weight-bold text-center"
          >
            ¡Solicitud enviada con éxito!
          </span>
        </v-card-title>
        <v-card-text class="text-center px-8 pb-4">
          <p class="text-body-1 greylight--text mb-4">
            Gracias por tu interés en TradeFlow.
            Recibimos tu solicitud de demo.
          </p>
          <p class="text-body-1 greydark--text font-weight-medium">
            Nuestro equipo de comercio exterior se pondrá
            en contacto en las próximas
            <strong>24 horas</strong> para coordinar tu
            demo personalizada.
          </p>
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn
            large
            depressed
            rounded
            class="white--text px-8"
            :style="{ backgroundColor: '#00695C' }"
            @click="showSuccessDialog = false"
          >
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog
      v-model="showErrorDialog"
      max-width="500"
    >
      <v-card class="rounded-lg">
        <v-card-title class="d-flex flex-column align-center pt-8">
          <v-avatar color="error" size="80" class="mb-4">
            <v-icon size="50" color="white">mdi-alert-circle</v-icon>
          </v-avatar>
          <span
            class="text-h5 greydark--text font-weight-bold text-center"
          >
            Error al enviar
          </span>
        </v-card-title>
        <v-card-text class="text-center px-8 pb-4">
          <p class="text-body-1 greylight--text mb-4">
            Lo sentimos, ocurrió un error al enviar
            tu solicitud.
          </p>
          <p class="text-body-1 greydark--text">
            Por favor, intenta de nuevo o contáctanos
            directamente a
            <strong>contacto@adaptifyx.com</strong>.
          </p>
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn
            color="error"
            large
            depressed
            rounded
            class="white--text px-8"
            @click="showErrorDialog = false"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import URI from 'urijs';

export default {
  name: 'TFContact',
  data() {
    return {
      submitting: false,
      showSuccessDialog: false,
      showErrorDialog: false,
      form: {
        name: '',
        company: '',
        email: '',
        phone: '',
        operationType: '',
        subject: '',
        message: '',
      },
      operationTypes: [
        'Exportador',
        'Importador',
        'Agente Aduanal',
        'Ambas operaciones (importación y exportación)',
        'No estoy seguro todavía',
      ],
      emailRules: [
        (v) => !!v || 'El correo electrónico es requerido',
        (v) => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(v)
          || 'Email debe ser válido',
      ],
      demoFeatures: [
        {
          icon: 'mdi-file-document-check',
          text: 'Generación de documentos con tus datos reales',
        },
        {
          icon: 'mdi-robot',
          text: 'Validación AI en vivo de tus facturas',
        },
        {
          icon: 'mdi-cash-register',
          text: 'Control de gastos configurado para tu operación',
        },
        {
          icon: 'mdi-calendar-clock',
          text: '15 minutos, sin compromiso',
        },
      ],
    };
  },
  methods: {
    submitForm() {
      if (!this.$refs.contactForm.validate()) {
        return;
      }

      this.submitting = true;

      const uri = new URI('https://contact.adaptifyx.com');
      uri.segment('items');

      const body = {
        name: this.form.name,
        email: this.form.email,
        subject: this.form.subject,
        message: `Empresa: ${this.form.company}\nTipo de operación: ${this.form.operationType}\n${this.form.phone ? `Teléfono: ${this.form.phone}\n` : ''}\n${this.form.message}`,
        source: 'tradeflow',
      };

      axios.put(uri.toString(), body, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(() => {
          this.showSuccessDialog = true;
          this.resetForm();
        })
        .catch(() => {
          this.showErrorDialog = true;
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    resetForm() {
      this.form = {
        name: '',
        company: '',
        email: '',
        phone: '',
        operationType: '',
        subject: '',
        message: '',
      };
      this.$refs.contactForm.resetValidation();
    },
  },
};
</script>

<style lang="scss" scoped>
.tf-contact-section {
  background-color: #F5F5F5;
}
</style>
